import { areShippingDimensionsOptional, isOpenSizingSellingMethodAllowed } from '../../product/checkAttribute';
import { getError, getNumberValue } from '..';
import { isValidNumber, toString } from '../../number';
import { clearErrors, setErrors } from '../../errors';
import { validateShipping } from '../../validations/shipping';
import { getSize } from '../../iterable/getSize';

import { SELLING_METHOD, SHIPPING_ERRORS, VALUE } from '../../../constants/product';
import { EBAY, ETSY, FAIRE } from '../../../constants/channels';
import { OPERATIONS } from '../../../constants/bulkEdit';
import { ATTRIBUTE } from '../../../constants/attributes';
import { STATUS } from '../../../constants/listings';
import { FORM } from '../../../constants/forms';

function getAttributeByOperation({ channel, operation }) {
  switch (channel) {
    case EBAY: {
      if (/^country/.test(operation)) {
        return ATTRIBUTE.COUNTRY;
      }

      if (/^location/.test(operation)) {
        return ATTRIBUTE.LOCATION;
      }

      if (/^postal_code/.test(operation)) {
        return ATTRIBUTE.POSTAL_CODE;
      }

      if (/^shipping_package_depth/.test(operation)) {
        return ATTRIBUTE.DEPTH;
      }

      if (/^shipping_package_length/.test(operation)) {
        return ATTRIBUTE.LENGTH;
      }

      if (/^shipping_package_major_weight/.test(operation)) {
        return ATTRIBUTE.WEIGHT_MAJOR;
      }

      if (/^shipping_package_minor_weight/.test(operation)) {
        return ATTRIBUTE.WEIGHT_MINOR;
      }

      if (/^shipping_package_width/.test(operation)) {
        return ATTRIBUTE.WIDTH;
      }

      return undefined;
    }

    case ETSY: {
      if (/^itemHeight/.test(operation)) {
        return ATTRIBUTE.HEIGHT;
      }

      if (/^itemLength/.test(operation)) {
        return ATTRIBUTE.LENGTH;
      }

      if (/^itemWeight/.test(operation)) {
        return ATTRIBUTE.WEIGHT;
      }

      if (/^itemWidth/.test(operation)) {
        return ATTRIBUTE.WIDTH;
      }

      return undefined;
    }

    default: {
      return undefined;
    }
  }
}

export default function shipping({ actions, operation, product: source, shopData }) {
  let product = source;
  const channel = product.get('channel');
  const operationType = operation.get('type');

  switch (channel) {
    case EBAY: {
      switch (operationType) {
        case OPERATIONS[EBAY].COUNTRY:
        case OPERATIONS[EBAY].LOCATION:
        case OPERATIONS[EBAY].POSTAL_CODE: {
          const type = getAttributeByOperation({ channel, operation: operationType });
          const operationValue = operation.get(VALUE);
          const productValue = product.getIn([FORM.SHIPPING, type, VALUE]);

          if (operationValue === productValue) break;

          const updatedShipping = product.get(FORM.SHIPPING).setIn([type, VALUE], operationValue);
          const errors = validateShipping({ channel, shipping: updatedShipping, shopData });

          if (errors.get(type)) break;

          product = clearErrors({
            errors: [type],
            path: FORM.SHIPPING,
            product: product.set(FORM.SHIPPING, updatedShipping),
          });

          break;
        }

        case OPERATIONS[EBAY].DEPTH.DECREASE_BY:
        case OPERATIONS[EBAY].DEPTH.INCREASE_BY:
        case OPERATIONS[EBAY].DEPTH.SET:
        case OPERATIONS[EBAY].LENGTH.DECREASE_BY:
        case OPERATIONS[EBAY].LENGTH.INCREASE_BY:
        case OPERATIONS[EBAY].LENGTH.SET:
        case OPERATIONS[EBAY].WIDTH.DECREASE_BY:
        case OPERATIONS[EBAY].WIDTH.INCREASE_BY:
        case OPERATIONS[EBAY].WIDTH.SET: {
          const type = getAttributeByOperation({ channel, operation: operationType });
          const operationValue = operation.get(VALUE) || 0;
          const productValue = product.getIn([FORM.SHIPPING, type, VALUE]) || 0;
          const value = getNumberValue({ operationType, operationValue, productValue });

          if (value === productValue) break;

          const updatedShipping = product.get(FORM.SHIPPING).setIn([type, VALUE], value);
          const errors = validateShipping({ channel, shipping: updatedShipping, shopData });

          if (errors.get(type)) break;

          product = clearErrors({
            errors: [type],
            path: FORM.SHIPPING,
            product: product.set(FORM.SHIPPING, updatedShipping),
          });

          break;
        }

        case OPERATIONS[EBAY].IRREGULAR: {
          product = product.setIn([FORM.SHIPPING, ATTRIBUTE.IRREGULAR, VALUE], operation.get(VALUE));
          break;
        }

        case OPERATIONS[EBAY].PAYMENT_POLICY: {
          const operationValue = operation.get(VALUE);
          const productValue = product.getIn([FORM.SHIPPING, ATTRIBUTE.PAYMENT_POLICY, VALUE]);

          if (operationValue === productValue) break;

          const updatedShipping = product
            .get(FORM.SHIPPING)
            .setIn([ATTRIBUTE.PAYMENT_POLICY, VALUE], operationValue);

          const errors = validateShipping({ channel, shipping: updatedShipping, shopData });

          if (!errors.get(ATTRIBUTE.PAYMENT_POLICY)) {
            product = clearErrors({
              errors: [ATTRIBUTE.PAYMENT_POLICY],
              path: FORM.SHIPPING,
              product: product.set(FORM.SHIPPING, updatedShipping),
            });
          }

          break;
        }

        case OPERATIONS[EBAY].RETURN_POLICY: {
          const operationValue = operation.get(VALUE);
          const productValue = product.getIn([FORM.SHIPPING, ATTRIBUTE.RETURN_POLICY, VALUE]);

          if (operationValue === productValue) break;

          const updatedShipping = product
            .get(FORM.SHIPPING)
            .setIn([ATTRIBUTE.RETURN_POLICY, VALUE], operationValue);

          const errors = validateShipping({ channel, shipping: updatedShipping, shopData });

          if (!errors.get(ATTRIBUTE.RETURN_POLICY)) {
            product = clearErrors({
              errors: [ATTRIBUTE.RETURN_POLICY],
              path: FORM.SHIPPING,
              product: product.set(FORM.SHIPPING, updatedShipping),
            });
          }

          break;
        }

        case OPERATIONS[EBAY].SHIPPING_POLICY: {
          const operationValue = operation.get(VALUE);
          const productValue = product.getIn([FORM.SHIPPING, ATTRIBUTE.SHIPPING_POLICY, VALUE]);

          if (operationValue === productValue) break;

          const updatedShipping = product
            .get(FORM.SHIPPING)
            .setIn([ATTRIBUTE.SHIPPING_POLICY, VALUE], operationValue);

          const errors = validateShipping({ channel, shipping: updatedShipping, shopData })
            .filter(function filterShippingErrors(error, attribute) {
              switch (attribute) {
                case ATTRIBUTE.LOCATION:
                case ATTRIBUTE.POSTAL_CODE:
                case ATTRIBUTE.SHIPPING_POLICY:
                case ATTRIBUTE.WEIGHT_MAJOR:
                case ATTRIBUTE.WEIGHT_MINOR: {
                  return true;
                }

                default: {
                  return false;
                }
              }
            });

          if (!errors.get(ATTRIBUTE.SHIPPING_POLICY)) {
            if (getSize(errors) > 0) {
              product = setErrors({
                errors,
                path: FORM.SHIPPING,
                product: product.set(FORM.SHIPPING, updatedShipping),
              });
            } else {
              product = clearErrors({
                errors: [
                  ATTRIBUTE.LOCATION,
                  ATTRIBUTE.POSTAL_CODE,
                  ATTRIBUTE.SHIPPING_POLICY,
                  ATTRIBUTE.WEIGHT_MAJOR,
                  ATTRIBUTE.WEIGHT_MINOR,
                ],
                path: FORM.SHIPPING,
                product: product.set(FORM.SHIPPING, updatedShipping),
              });
            }
          }

          break;
        }

        case OPERATIONS[EBAY].WEIGHT_MAJOR.DECREASE_BY:
        case OPERATIONS[EBAY].WEIGHT_MAJOR.INCREASE_BY:
        case OPERATIONS[EBAY].WEIGHT_MAJOR.SET:
        case OPERATIONS[EBAY].WEIGHT_MINOR.DECREASE_BY:
        case OPERATIONS[EBAY].WEIGHT_MINOR.INCREASE_BY:
        case OPERATIONS[EBAY].WEIGHT_MINOR.SET: {
          const type = getAttributeByOperation({ channel, operation: operationType });
          const operationValue = operation.get(VALUE) || 0;
          const productValue = product.getIn([FORM.SHIPPING, type, VALUE]) || 0;
          const value = getNumberValue({ operationType, operationValue, productValue });

          if (value === productValue) break;

          const updatedShipping = product.get(FORM.SHIPPING).setIn([type, VALUE], value);
          const errors = validateShipping({ channel, shipping: updatedShipping, shopData })
            .filter(function filterShippingErrors(error, attribute) {
              switch (attribute) {
                case ATTRIBUTE.WEIGHT_MAJOR:
                case ATTRIBUTE.WEIGHT_MINOR: {
                  return true;
                }

                default: {
                  return false;
                }
              }
            });

          if (!errors.get(type)) {
            if (getSize(errors) > 0) {
              product = setErrors({
                errors,
                path: FORM.SHIPPING,
                product: product.set(FORM.SHIPPING, updatedShipping),
              });
            } else {
              product = clearErrors({
                errors: [ATTRIBUTE.WEIGHT_MAJOR, ATTRIBUTE.WEIGHT_MINOR],
                path: FORM.SHIPPING,
                product: product.set(FORM.SHIPPING, updatedShipping),
              });
            }
          }

          break;
        }

        default: {
          break;
        }
      }

      break;
    }

    case ETSY: {
      switch (operationType) {
        case OPERATIONS[ETSY].RETURN_POLICY: {
          if (product.getIn([FORM.DETAILS, ATTRIBUTE.DIGITAL, VALUE])) break;

          const operationValue = operation.get(VALUE);
          const productValue = product.getIn([FORM.SHIPPING, ATTRIBUTE.RETURN_POLICY, VALUE]);

          if (operationValue === productValue) break;

          const updatedShipping = product
            .get(FORM.SHIPPING)
            .setIn([ATTRIBUTE.RETURN_POLICY, VALUE], operationValue);

          const errors = validateShipping({ channel, shipping: updatedShipping, shopData });

          if (!errors.get(ATTRIBUTE.RETURN_POLICY)) {
            product = clearErrors({
              errors: [ATTRIBUTE.RETURN_POLICY],
              path: FORM.SHIPPING,
              product: product.set(FORM.SHIPPING, updatedShipping),
            });
          }

          break;
        }

        case OPERATIONS[ETSY].SHIPPING_PROFILE: {
          if (product.getIn([FORM.DETAILS, ATTRIBUTE.DIGITAL, VALUE])) break;

          const operationValue = operation.get(VALUE);
          const productValue = product.getIn([FORM.SHIPPING, ATTRIBUTE.PROFILE_ID, VALUE]);

          if (operationValue === productValue) break;

          const profileType = shopData.getIn(['shippingProfiles', 'byId', operationValue, 'profileType']);
          const updatedShipping = product
            .get(FORM.SHIPPING)
            .setIn([ATTRIBUTE.PROFILE_ID, VALUE], operationValue)
            .setIn([ATTRIBUTE.PROFILE_TYPE, VALUE], profileType);

          const errors = validateShipping({ channel, shipping: updatedShipping, shopData });

          if (getSize(errors) === 0) {
            product = clearErrors({
              errors: SHIPPING_ERRORS[channel],
              path: FORM.SHIPPING,
              product: product.set(FORM.SHIPPING, updatedShipping),
            });
          }

          break;
        }

        case OPERATIONS[ETSY].HEIGHT.DECREASE_BY:
        case OPERATIONS[ETSY].HEIGHT.INCREASE_BY:
        case OPERATIONS[ETSY].HEIGHT.SET:
        case OPERATIONS[ETSY].LENGTH.DECREASE_BY:
        case OPERATIONS[ETSY].LENGTH.INCREASE_BY:
        case OPERATIONS[ETSY].LENGTH.SET:
        case OPERATIONS[ETSY].WEIGHT.DECREASE_BY:
        case OPERATIONS[ETSY].WEIGHT.INCREASE_BY:
        case OPERATIONS[ETSY].WEIGHT.SET:
        case OPERATIONS[ETSY].WIDTH.DECREASE_BY:
        case OPERATIONS[ETSY].WIDTH.INCREASE_BY:
        case OPERATIONS[ETSY].WIDTH.SET: {
          if (product.getIn([FORM.DETAILS, ATTRIBUTE.DIGITAL, VALUE])) break;

          const type = getAttributeByOperation({ channel, operation: operationType });
          const operationValue = operation.get(VALUE) || 0;
          const productValue = product.getIn([FORM.SHIPPING, type, VALUE]) || 0;
          const value = getNumberValue({ operationType, operationValue, productValue });

          if (value === productValue) break;

          const validatorProps = {
            allowEmpty: areShippingDimensionsOptional(product.get(FORM.SHIPPING)),
          };

          validatorProps.allowZero = validatorProps.allowEmpty;

          if (type === ATTRIBUTE.LENGTH) {
            validatorProps[ATTRIBUTE.HEIGHT] = product.getIn([FORM.SHIPPING, ATTRIBUTE.HEIGHT, VALUE]);
            validatorProps[ATTRIBUTE.WIDTH] = product.getIn([FORM.SHIPPING, ATTRIBUTE.WIDTH, VALUE]);
          } else {
            validatorProps[ATTRIBUTE.LENGTH] = product.getIn([FORM.SHIPPING, ATTRIBUTE.LENGTH, VALUE]);
          }

          if (getError({ channel, type, value, ...validatorProps })) break;

          product = clearErrors({
            errors: [type],
            path: FORM.SHIPPING,
            product: product.setIn([FORM.SHIPPING, type, VALUE], value),
          });

          break;
        }

        default: {
          break;
        }
      }

      break;
    }

    case FAIRE: {
      switch (operationType) {
        case OPERATIONS[FAIRE].CASE_SIZE.SET: {
          const value = operation.get('value');
          let productShipping = product.get(FORM.SHIPPING);

          if (isValidNumber(value) && value > 1) {
            productShipping = productShipping
              .setIn([ATTRIBUTE.CASE_SIZE, VALUE], toString(value))
              .setIn([ATTRIBUTE.SELLING_METHOD, VALUE], SELLING_METHOD.CASE);
          } else {
            productShipping = productShipping.setIn([ATTRIBUTE.CASE_SIZE, VALUE], '');

            if (productShipping.getIn([ATTRIBUTE.SELLING_METHOD, VALUE]) === SELLING_METHOD.CASE) {
              productShipping = productShipping.setIn([ATTRIBUTE.SELLING_METHOD, VALUE], SELLING_METHOD.ITEM);
            }
          }

          const errors = validateShipping({
            allowZero: (
              product.get('status') !== STATUS[FAIRE].PUBLISHED &&
              product.get('status') !== STATUS[FAIRE].UNPUBLISHED
            ),
            channel,
            shipping: productShipping,
          });

          if (errors.get(ATTRIBUTE.CASE_SIZE)) break;

          product = clearErrors({
            errors: [ATTRIBUTE.CASE_SIZE],
            path: FORM.SHIPPING,
            product: product.set(FORM.SHIPPING, productShipping),
          });

          break;
        }

        case OPERATIONS[FAIRE].MINIMUM_ORDER_QUANTITY.SET: {
          const value = operation.get('value');
          const productShipping = product
            .get(FORM.SHIPPING)
            .setIn([ATTRIBUTE.MINIMUM_ORDER_QUANTITY, VALUE], toString(value) || '');

          const errors = validateShipping({
            allowZero: (
              product.get('status') !== STATUS[FAIRE].PUBLISHED &&
              product.get('status') !== STATUS[FAIRE].UNPUBLISHED
            ),
            channel,
            shipping: productShipping,
          });

          if (errors.get(ATTRIBUTE.MINIMUM_ORDER_QUANTITY)) break;

          product = clearErrors({
            errors: [ATTRIBUTE.MINIMUM_ORDER_QUANTITY],
            path: FORM.SHIPPING,
            product: product.set(FORM.SHIPPING, productShipping),
          });

          break;
        }

        case OPERATIONS[FAIRE].PER_STYLE_MINIMUM_ORDER_QUANTITY.SET: {
          const value = operation.get('value');
          let productShipping = product.get(FORM.SHIPPING);

          if (isValidNumber(value) && value > 1 && isOpenSizingSellingMethodAllowed(product.get(FORM.VARIATIONS))) {
            productShipping = productShipping
              .setIn([ATTRIBUTE.PER_STYLE_MINIMUM_ORDER_QUANTITY, VALUE], toString(value))
              .setIn([ATTRIBUTE.SELLING_METHOD, VALUE], SELLING_METHOD.SIZE);
          } else {
            productShipping = productShipping.setIn([ATTRIBUTE.PER_STYLE_MINIMUM_ORDER_QUANTITY, VALUE], '');

            if (productShipping.getIn([ATTRIBUTE.SELLING_METHOD, VALUE]) === SELLING_METHOD.SIZE) {
              productShipping = productShipping.setIn([ATTRIBUTE.SELLING_METHOD, VALUE], SELLING_METHOD.ITEM);
            }
          }

          const errors = validateShipping({
            allowZero: (
              product.get('status') !== STATUS[FAIRE].PUBLISHED &&
              product.get('status') !== STATUS[FAIRE].UNPUBLISHED
            ),
            channel,
            shipping: productShipping,
          });

          if (errors.get(ATTRIBUTE.PER_STYLE_MINIMUM_ORDER_QUANTITY)) break;

          product = clearErrors({
            errors: [ATTRIBUTE.PER_STYLE_MINIMUM_ORDER_QUANTITY],
            path: FORM.SHIPPING,
            product: product.set(FORM.SHIPPING, productShipping),
          });

          break;
        }

        default: {
          break;
        }
      }

      break;
    }

    default: {
      break;
    }
  }

  return { actions, product };
}
